import ListItemRenderer from './list-item-renderer';
import SelectEvent from './select-event';

/**
 * The base ItemRenderer component you extend when creating your own visual representation of an Item the user can pick.
 *
 * A {@link ListItemRenderer} with a selection on top: it is handed an Item like any row is, and answers a click by selecting itself and saying so with a {@link SelectEvent}, which is what a {@link SelectList} keeps its selection from.
 *
 * The selection is written out here rather than taken from {@link SelectableContainer}, which holds the same three members for components that are selectable without being rows — a CheckBox, an option in an HTML select. A class has one parent, and this one's is ListItemRenderer, since being a row is what a SelectList
 * needs of it. The two are kept in step by hand; neither has moved since it was written.
 */
export default class SelectListItemRenderer<Item> extends ListItemRenderer<Item> {
	/**
	 * The payload value will be sent along with the SelectEvent as the payload property.
	 *
	 * @param {null | Item} value The payload value
	 */
	public set payload(value: Item | null) {
		this.#payload = value;
	}

	public get payload(): Item | null {
		return this.#payload;
	}

	/**
	 * The selected property is set to true when the ItemRenderer is clicked or Space / Enter is released while focused.
	 *
	 * When the selected property is set from within the ItemRenderer, a SelectEvent is dispatched that bubbles up through the DOM tree.
	 *
	 * @param {boolean} value True if selected, false otherwise
	 */
	public set selected(value: boolean) {
		if (this.#selected === value) {
			return;
		}
		assertIsBoolean(value);
		this.#selected = value;
		this.#selectedChanged = true;
		this.invalidateProperties();
	}

	public get selected(): boolean {
		return this.#selected;
	}

	#payload: Item | null;
	#selected: boolean;
	#selectedChanged: boolean;

	public constructor() {
		super();
		this.#payload = null;
		this.#selected = false;
		this.#selectedChanged = false;
	}

	protected override clicked(event: Event): void {
		super.clicked(event);
		this.selected = !this.selected;
		this.#dispatchSelectEvent();
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#selectedChanged) {
			this.#commitSelected();
		}
	}

	/**
	 * The selectedChanged method will be invoked every time the selected property changes.
	 *
	 * Override the method in sub classes to specify how the Component will behave or be styled.
	 */
	protected selectedChanged(): void {
		// override in sub classes
	}

	#commitSelected(): void {
		this.#selectedChanged = false;
		this.selectedChanged();
	}

	#dispatchSelectEvent(): void {
		this.dispatchEvent(new SelectEvent<Item>(this.selected, this.payload));
	}
}
customElements.define('ac-select-list-item-renderer', SelectListItemRenderer);

function assertIsBoolean(value: unknown): asserts value is boolean {
	if (typeof value === 'boolean') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be of type boolean`);
}
